import React, { useCallback, useRef, useState } from 'react';
import { emitAck } from '../api/socket.js';
import { HandIcon, CheckIcon, CloseIcon } from './Icons.jsx';

const EMOJIS = ['❤️', '👍', '😮', '🔥', '🎉'];
const DOUBLE_TAP_MS = 300;
const LONG_PRESS_MS = 450;
const SIGNAL_GAP_MS = 2500;

/**
 * What a learner sees while the teacher is on a slide (no live question):
 * eyes-front, plus the lightweight ways to take part — understand-meter,
 * raise hand, and TikTok-style double-tap reactions.
 */
export default function StudentSlideView({ lessonUnit }) {
  const [understand, setUnderstand] = useState(null); // transient 'ok' | 'confused'
  const [handSent, setHandSent] = useState(false);
  const [tray, setTray] = useState(false);
  const [hearts, setHearts] = useState([]);
  const cooldown = useRef({ understand: 0, hand: 0, react: 0 });
  const tap = useRef({ at: 0, timer: null, longFired: false });

  const send = (kind, value) => {
    const now = Date.now();
    if (now - (cooldown.current[kind] || 0) < (kind === 'react' ? 350 : SIGNAL_GAP_MS)) return false;
    cooldown.current[kind] = now;
    emitAck('student:signal', { kind, value }).catch(() => {});
    return true;
  };

  const vote = (value) => {
    if (send('understand', value)) {
      setUnderstand(value);
      setTimeout(() => setUnderstand(null), SIGNAL_GAP_MS);
    }
  };

  const raise = () => {
    if (send('hand')) {
      setHandSent(true);
      setTimeout(() => setHandSent(false), SIGNAL_GAP_MS);
    }
  };

  const fling = useCallback((emoji, x, y) => {
    if (!send('react', emoji)) return;
    const id = Math.random().toString(36).slice(2);
    setHearts((h) => [...h, { id, emoji, x, y }]);
    setTimeout(() => setHearts((h) => h.filter((k) => k.id !== id)), 1000);
  }, []);

  const onPointerDown = (e) => {
    tap.current.longFired = false;
    tap.current.timer = setTimeout(() => {
      tap.current.longFired = true;
      setTray(true);
    }, LONG_PRESS_MS);
  };
  const onPointerUp = (e) => {
    clearTimeout(tap.current.timer);
    if (tap.current.longFired) return;
    const now = Date.now();
    if (now - tap.current.at < DOUBLE_TAP_MS) {
      tap.current.at = 0;
      fling('❤️', e.clientX, e.clientY);
    } else {
      tap.current.at = now;
    }
  };

  return (
    <div
      className="ssv"
      onPointerDown={onPointerDown}
      onPointerUp={onPointerUp}
      onPointerLeave={() => clearTimeout(tap.current.timer)}
    >
      <div className="ssv-eyes">
        <span className="ssv-unit">{lessonUnit}</span>
        <h2>ดูที่จอด้านหน้า</h2>
        <p className="text-dim">แตะสองครั้งเพื่อส่ง ❤️ · แตะค้างเพื่อเลือกอีโมจิอื่น</p>
      </div>

      <div className="ssv-meter">
        <button
          className={`ssv-meter-btn is-ok ${understand === 'ok' ? 'is-active' : ''}`}
          onPointerUp={(e) => { e.stopPropagation(); vote('ok'); }}
        >
          {understand === 'ok' ? <><CheckIcon size={18} /> ส่งแล้ว</> : 'เข้าใจ'}
        </button>
        <button
          className={`ssv-meter-btn is-confused ${understand === 'confused' ? 'is-active' : ''}`}
          onPointerUp={(e) => { e.stopPropagation(); vote('confused'); }}
        >
          {understand === 'confused' ? <><CheckIcon size={18} /> ส่งแล้ว</> : 'ยังงง'}
        </button>
      </div>

      <button
        className={`ssv-hand ${handSent ? 'is-sent' : ''}`}
        onPointerUp={(e) => { e.stopPropagation(); raise(); }}
      >
        <HandIcon size={18} />
        {handSent ? 'ยกมือแล้ว — รอผู้สอน' : 'ยกมือถาม'}
      </button>

      {tray && (
        <div className="ssv-tray" onPointerUp={(e) => e.stopPropagation()}>
          <button className="ssv-tray-close" onClick={() => setTray(false)} aria-label="ปิด"><CloseIcon size={16} /></button>
          {EMOJIS.map((em) => (
            <button key={em} onPointerUp={(e) => { e.stopPropagation(); fling(em, e.clientX, e.clientY); setTray(false); }}>{em}</button>
          ))}
        </div>
      )}

      <div className="ssv-hearts" aria-hidden="true">
        {hearts.map((h) => (
          <span key={h.id} style={{ left: h.x, top: h.y }}>{h.emoji}</span>
        ))}
      </div>
    </div>
  );
}
